// GENERADO — no editar a mano. Origen: backend/src/oficina/compartido/.
// Regenerar: node scripts/gen-oficina-compartido.mjs

// Aparcar (Pau aprobó el 04-11, web/qa/auditoria-apple/plan.md «Lo que decide Pau» 1): un agente con el que nadie ha
// hablado en 24 h (ni Pau a él ni él a Pau) y que no está trabajando se aparca solo; Pau también puede aparcarlo o
// despertarlo a mano. Aparcado = duerme en su mesa, sin insignia, no cuenta como «te toca» y va al bloque plegado
// «Aparcados» de la lista; a los 7 días aparcado se le propone a Pau cerrarlo (una pista, nunca una acción).
//
// SE CALCULA AL LEER, nunca se escribe: ningún barrido ni cron mueve este estado. Las únicas escrituras son las dos
// intenciones de Pau a mano (`oficina_contratado.aparcado_en` / `despertado_en`, migración 107); lo demás son hechos
// que ya existen (sus mensajes, los de Pau, sus peticiones). La lista lo vuelve a evaluar con su propio reloj, así un
// agente se aparca a las 24 h aunque el servidor no mande nada.
//
// La urgencia nunca se esconde: cualquier cosa NUEVA del agente (un mensaje, un parte, una pregunta, un permiso, un
// «no puedo seguir») mueve la referencia y lo despierta; lo que lleva 24 h sin respuesta es justo lo que se aparca.
// Lo que espera a un tercero o tiene botón (una subida suya en la bandeja, una tarjeta de Santi esperando tu clic) y el
// que tiene un contrato con Santi (ya va plegado en «Con Santi») no se aparcan. Código PURO (se copia a la web).
import type { Situacion } from './vocabulario.js';

export const HORAS_PARA_APARCAR = 24;
export const DIAS_PARA_PROPONER_CERRAR = 7;
const HORA_MS = 3_600_000;

export interface EntradaAparcar {
  situacion: Situacion;
  creadoEn: string;
  /** Lo último de Pau a este agente (lo que le escribió o un permiso que contestó); null = nunca. */
  ultimoDePauEn: string | null;
  /** Pau lo aparcó a mano (la última vez); null = nunca. */
  aparcadoEn: string | null;
  /** Pau lo despertó a mano (la última vez); null = nunca. */
  despertadoEn: string | null;
  /** Lo último del agente: su mensaje, su parte y su última petición (pregunta, plan, permiso, cierre). */
  ultimoMensaje: { en: string } | null;
  ultimoInformeEn: string | null;
  teNecesitaDesde: string | null;
}

/** Lo que solo sabe la web (la bandeja de subidas y los contratos con Santi); el servidor no lo pasa. */
export interface ContextoAparcar {
  subida?: boolean;
  conSanti?: boolean;
  esperaTuSi?: boolean;
}

export interface Aparcado {
  /** Desde cuándo duerme. */
  desde: string;
  /** Quién: Pau a mano, o solo (24 h sin hablar). */
  por: 'pau' | 'auto';
  /** Lleva 7 días o más: proponer a Pau cerrarlo. */
  proponerCerrar: boolean;
}

/** Trabajando, empezando, cerrando o yéndose: nunca se aparca (no está quieto). */
const EN_MOVIMIENTO: readonly Situacion[] = ['trabajando', 'llegando', 'cerrando', 'saliendo'];

const ms = (iso: string | null | undefined): number => {
  const t = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

/** La última vez que hubo conversación (en cualquier sentido) o que Pau lo despertó: desde ahí cuentan las 24 h. */
export function referenciaAparcar(x: EntradaAparcar): number {
  return Math.max(ms(x.creadoEn), ms(x.ultimoDePauEn), ms(x.despertadoEn), ms(x.ultimoMensaje?.en), ms(x.ultimoInformeEn), ms(x.teNecesitaDesde));
}

function puedeDormir(x: Pick<EntradaAparcar, 'situacion'>, ctx: ContextoAparcar): boolean {
  return !EN_MOVIMIENTO.includes(x.situacion) && !ctx.subida && !ctx.esperaTuSi && !ctx.conSanti;
}

/** ¿Está aparcado ahora? null = despierto. */
export function aparcado(x: EntradaAparcar, ahoraMs: number, ctx: ContextoAparcar = {}): Aparcado | null {
  if (!puedeDormir(x, ctx)) return null;
  const ref = referenciaAparcar(x);
  if (!Number.isFinite(ref)) return null;
  const manual = ms(x.aparcadoEn);
  // A mano: vale mientras sea lo último que ha pasado (escribirle, despertarlo o algo nuevo suyo lo anula).
  const desde = manual >= ref ? manual : ref + HORAS_PARA_APARCAR * HORA_MS <= ahoraMs ? ref + HORAS_PARA_APARCAR * HORA_MS : null;
  if (desde === null) return null;
  return {
    desde: new Date(desde).toISOString(),
    por: manual >= ref ? 'pau' : 'auto',
    proponerCerrar: ahoraMs - desde >= DIAS_PARA_PROPONER_CERRAR * 24 * HORA_MS,
  };
}

/**
 * El próximo instante en que su estado cambia solo con el tiempo (se aparca, o pasa a «proponer cerrar»); null si no
 * cambia sin que pase algo. La web pone UN temporizador al más cercano: sin sondeos.
 */
export function proximoCambioAparcar(x: EntradaAparcar, ahoraMs: number, ctx: ContextoAparcar = {}): number | null {
  if (!puedeDormir(x, ctx)) return null;
  const ref = referenciaAparcar(x);
  if (!Number.isFinite(ref)) return null;
  const a = aparcado(x, ahoraMs, ctx);
  if (!a) return ref + HORAS_PARA_APARCAR * HORA_MS;
  if (a.proponerCerrar) return null;
  return Date.parse(a.desde) + DIAS_PARA_PROPONER_CERRAR * 24 * HORA_MS;
}
